import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";
import Seo from "@/components/Seo";

export default function VerifyEmail() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const autoSent = useRef(false);
  // Only same-site paths — `next` comes from the URL, so don't let it become an open redirect.
  const next = /^\/(?!\/)/.test(params.get("next") || "") ? params.get("next") : "/checkout";
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  // Registration emails a code on its own, but a user sent here from the cart/checkout gate or
  // their profile has usually outlived that 15-minute code (or never saw it) — send a fresh one.
  useEffect(() => {
    if (params.get("send") !== "1" || !user?.email || user.email_verified || autoSent.current) return;
    autoSent.current = true;
    api.resendVerification(user.email)
      .then(() => toast.success("We've emailed you a new verification code."))
      .catch((e) => toast.error(e.message || "Could not send verification code"));
  }, [params, user]);

  const submit = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return toast.error("Enter the 6-digit code from your email");
    if (!user?.email) return toast.error("Please sign in again to verify your email");
    setLoading(true);
    try {
      await api.verifyEmail(user.email, code);
      await refresh();
      toast.success("Email verified");
      nav(next);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (!user?.email) return;
    setResending(true);
    try {
      await api.resendVerification(user.email);
      setCode("");
      toast.success("Verification code sent. Check your email.");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setResending(false);
    }
  };

  return (
    <div data-testid="verify-email-page" className="mx-auto max-w-md px-4 py-16">
      <Seo title="Verify your email" path="/verify-email" noindex={true} />
      <div className="text-center">
        <div className="mx-auto w-12 h-12 rounded-full bg-[var(--yp-primary)] text-white grid place-items-center"><MailCheck /></div>
        <h1 className="font-serif-display text-3xl text-[var(--yp-primary)] mt-4">Verify your email</h1>
        <p className="text-[var(--yp-muted)] text-sm mt-1">
          {user?.email
            ? `Enter the 6-digit code we sent to ${user.email}. You'll need this before you can place an order.`
            : "Enter the 6-digit verification code we sent you."}
        </p>
      </div>
      <form onSubmit={submit} className="mt-8 bg-white border border-[var(--yp-border)] rounded-2xl p-6 space-y-4">
        <div>
          <label className="text-xs uppercase tracking-widest text-[var(--yp-muted)]">Verification code</label>
          <input
            data-testid="verify-code"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            autoComplete="one-time-code"
            autoCapitalize="off"
            autoCorrect="off"
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="mt-1 w-full border border-[var(--yp-border)] rounded-xl px-3 py-3 text-2xl tracking-[0.5em] text-center font-semibold outline-none focus:border-[var(--yp-primary)]"
          />
        </div>
        <button data-testid="verify-submit" disabled={loading || code.length !== 6} className="w-full btn-primary rounded-full py-3 text-sm font-medium disabled:opacity-70">
          {loading ? "Verifying…" : "Verify Email"}
        </button>
        <div className="text-center text-sm text-[var(--yp-muted)]">
          Didn't get a code?{" "}
          <button type="button" data-testid="resend-verification" onClick={resend} disabled={resending} className="text-[var(--yp-primary)] hover:underline font-medium">
            {resending ? "Sending…" : "Resend"}
          </button>
        </div>
      </form>
    </div>
  );
}
